import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Target } from "lucide-react";

export default function Module() {
  const { moduleId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: p } = await api.get("/progress");
      setData(p);
    })();
  }, []);

  const mod = data?.module_progress?.find((m) => m.id === moduleId);

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Loading module…</div>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-zinc-700">Module not found.</p>
          <Link to="/dashboard" className="text-red-600 font-semibold mt-2 inline-block">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid={`module-page-${moduleId}`}>
        <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="back-to-dashboard-link">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        <div className="overline mb-3"><span className="red-bar" />Module {mod.id.replace("m", "")}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">{mod.title}</h1>
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-6">
          {mod.hours} hours · {mod.lessons.length} lesson{mod.lessons.length > 1 ? "s" : ""}
        </div>

        <div className="card-tech p-5 mb-10">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-semibold">Progress</span>
            <span className="font-mono">{mod.percent}% · {mod.hours_completed} / {mod.hours} hrs</span>
          </div>
          <Progress value={mod.percent} className="h-2 rounded-none bg-zinc-100 [&>div]:bg-red-600" />
        </div>

        <div className="space-y-3">
          {mod.lessons.map((l, i) => (
            <Link
              to={`/lesson/${l.id}`}
              key={l.id}
              className="card-tech p-6 grid grid-cols-12 gap-3 items-center group"
              data-testid={`module-lesson-${l.id}`}
            >
              <div className="col-span-1">
                {l.completed ? <CheckCircle2 className="w-6 h-6 text-green-600" /> : <Circle className="w-6 h-6 text-zinc-300" />}
              </div>
              <div className="col-span-9">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Lesson {i + 1}</div>
                <div className="font-heading text-xl font-bold tracking-tight group-hover:text-red-600 transition-colors">{l.title}</div>
                <div className="text-xs font-mono text-zinc-500 mt-1">{l.hours} hrs</div>
              </div>
              <div className="col-span-2 text-right">
                <ArrowRight className="w-5 h-5 ml-auto text-zinc-400 group-hover:text-red-600 transition-all group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 card-tech p-6 flex items-center gap-4" data-testid="module-objective-card">
          <Target className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">Finish every lesson + quiz to complete this module.</div>
            <div className="text-sm text-zinc-600 mt-1">Quizzes are graded automatically; your best score counts.</div>
          </div>
        </div>
      </main>
    </div>
  );
}
